import React from 'react';
import { Modal, Descriptions, Space, Typography, Button, Image } from 'antd';
import { UserContent } from '@models/user-contents';
import moment from 'moment';
import { Stars } from './Stars';
import { Linkify } from '@components/common/Linkify';
import { cdnImageUrl } from '@utils/image';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import ClipLoader from 'react-spinners/ClipLoader';
import { WEB_URL } from '@variables';

interface ReviewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (userContentId: string) => void;
  onExclude?: (userContentId: string) => void;
  onNavigate?: (userContentId: string) => void;
  reviewDetail: {
    user: {
      name: string;
      reviewCount: number;
      bestReviewCount: number;
      paymentStatementCount: number;
      paymentStatementAmount: number;
    };
    content: {
      viewCount: number;
      shareCount: number;
      favoriteCount: number;
    };
    userContent: UserContent;
    prevUserContentId?: string;
    nextUserContentId?: string;
    currentIndex: number;
    totalCount: number;
    isSelected?: boolean;
  };
}

const RATE_LABELS = {
  rate1: '친화력',
  rate2: '전문성',
  rate3: '준비성',
  rate4: '시간준수',
  rate5: '아이 만족도',
};

export const ReviewDetailModal: React.FC<ReviewDetailModalProps> = ({
  isOpen,
  onClose,
  reviewDetail,
  onSelect,
  onExclude,
  onNavigate,
}) => {
  const { user, content, userContent, prevUserContentId, nextUserContentId, currentIndex, totalCount, isSelected } =
    reviewDetail || {};

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>리뷰 상세</span>
            {userContent ? (
              <span style={{ fontSize: '14px', color: '#666' }}>{`${currentIndex + 1} / ${totalCount}`}</span>
            ) : (
              <ClipLoader size={24} color="#1890ff" />
            )}
            <Button
              onClick={() => {
                window.open(`${WEB_URL}/lounge/${userContent?._id}`, '_blank');
              }}>
              리뷰 바로가기
            </Button>
            <Button
              onClick={() => {
                window.open(`${WEB_URL}/product/${userContent?.product?._id}`, '_blank');
              }}>
              상품 바로가기
            </Button>
          </div>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            icon={<LeftOutlined />}
            disabled={!prevUserContentId}
            onClick={() => {
              if (prevUserContentId && onNavigate) {
                onNavigate(prevUserContentId);
              }
            }}>
            이전 리뷰
          </Button>
          {userContent && (
            <div style={{ display: 'flex', gap: '8px' }}>
              {isSelected ? (
                <Button danger onClick={() => onExclude?.(userContent._id)}>
                  베스트 후보에서 제외
                </Button>
              ) : (
                <Button type="primary" onClick={() => onSelect?.(userContent._id)}>
                  베스트 후보 등록
                </Button>
              )}
            </div>
          )}
          <Button
            icon={<RightOutlined />}
            disabled={!nextUserContentId}
            onClick={() => {
              if (nextUserContentId && onNavigate) {
                onNavigate(nextUserContentId);
              }
            }}>
            다음 리뷰
          </Button>
        </div>
      }
      width={800}>
      {userContent ? (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Descriptions
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>리뷰 정보</span>
                {userContent?.product?.sellerName && (
                  <span style={{ color: '#888', fontWeight: 400, fontSize: 14 }}>
                    | {userContent.product.sellerName}
                  </span>
                )}
                {userContent?.product?.title && (
                  <span style={{ color: '#888', fontWeight: 400, fontSize: 14 }}>| {userContent.product.title}</span>
                )}
              </span>
            }
            bordered
            column={2}
            size="small">
            <Descriptions.Item label="조회수" labelStyle={{ width: '100px' }}>
              {content?.viewCount.toLocaleString()}회
            </Descriptions.Item>
            <Descriptions.Item label="공유수" labelStyle={{ width: '100px' }}>
              {content?.shareCount.toLocaleString()}회
            </Descriptions.Item>
            <Descriptions.Item label="좋아요" labelStyle={{ width: '100px' }}>
              {content?.favoriteCount.toLocaleString()}개
            </Descriptions.Item>
            <Descriptions.Item label="작성일" labelStyle={{ width: '100px' }}>
              {moment(userContent?.createdAt).format('YYYY.MM.DD HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="리뷰 내용" labelStyle={{ width: '100px' }} span={2}>
              <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', maxHeight: '280px', overflow: 'auto' }}>
                <div className="ProseMirror">
                  <Linkify>{userContent?.text}</Linkify>
                </div>
              </Typography.Paragraph>
            </Descriptions.Item>
            {userContent?.images && userContent.images.length > 0 && (
              <Descriptions.Item label="이미지" labelStyle={{ width: '100px' }} span={2}>
                <Space wrap size={4}>
                  {userContent.images.map((image, index) => (
                    <Image
                      key={index}
                      src={cdnImageUrl(image)}
                      alt={`리뷰 이미지 ${index + 1}`}
                      style={{ width: 100, height: 100, objectFit: 'cover' }}
                      preview={{
                        mask: '크게 보기',
                        maskClassName: 'custom-image-mask',
                        getContainer: () => document.querySelector('.ant-modal-content')!,
                      }}
                    />
                  ))}
                </Space>
              </Descriptions.Item>
            )}
          </Descriptions>
          {userContent?.rates && (
            <Descriptions title="평가 정보" bordered column={2} size="small">
              {Object.entries(userContent.rates).map(([key, value]) => (
                <Descriptions.Item
                  key={key}
                  label={RATE_LABELS[key as keyof typeof RATE_LABELS]}
                  labelStyle={{ width: '100px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Stars value={value} />
                    <span>{value}점</span>
                  </div>
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}
          <Descriptions title="작성자 정보" bordered column={2} size="small">
            <Descriptions.Item label="이름" labelStyle={{ width: '140px' }}>
              {user?.name}
            </Descriptions.Item>
            <Descriptions.Item label="총 리뷰 수" labelStyle={{ width: '140px' }}>
              {user?.reviewCount.toLocaleString()}개
            </Descriptions.Item>
            <Descriptions.Item label="총 베스트 리뷰 수" labelStyle={{ width: '140px' }}>
              {user?.bestReviewCount.toLocaleString()}개
            </Descriptions.Item>
            <Descriptions.Item label="총 결제 건수" labelStyle={{ width: '140px' }}>
              {user?.paymentStatementCount.toLocaleString()}건
            </Descriptions.Item>
            <Descriptions.Item label="총 결제 금액" labelStyle={{ width: '140px' }}>
              {user?.paymentStatementAmount.toLocaleString()}원
            </Descriptions.Item>
          </Descriptions>
        </Space>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <ClipLoader size={24} color="#1890ff" />
        </div>
      )}
    </Modal>
  );
};
